import { useCallback, useMemo, type MouseEvent as ReactMouseEvent } from 'react'
import { showNotification } from '@mantine/notifications'
import { parseApiDateUtc } from '@/lib/api-date'
import { ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import { findTabForDestination } from '@/lib/workspace-tab-route-match'
import type { ProjectRow, TicketDetailTicket, TicketListItem } from '@/types/api'

type WorkspaceTabLike = {
  id: string
  pathname: string
  search: string
}

type UseTicketPreviousTicketsParams = {
  currentTicket: TicketDetailTicket | null
  previousTickets: TicketListItem[]
  workspaceProjects: ProjectRow[]
  tabs: WorkspaceTabLike[]
  activeTabId: string | null | undefined
  previewLimit: number
  openOrSelectTab: (destination: string) => void
  closePreviousTicketsModal: () => void
}

export function useTicketPreviousTickets({
  currentTicket,
  previousTickets,
  workspaceProjects,
  tabs,
  activeTabId,
  previewLimit,
  openOrSelectTab,
  closePreviousTicketsModal,
}: UseTicketPreviousTicketsParams) {
  const openPreviousTicketInNewTab = useCallback((ticket: TicketListItem, event: ReactMouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    const path = `/tickets/${encodeURIComponent(ticketPathSegmentFromListItem(ticket))}`
    window.open(path, '_blank', 'noopener,noreferrer')
  }, [])

  const onSelectPreviousTicket = useCallback(
    (ticket: TicketListItem) => {
      if (ticket.id === currentTicket?.id) {
        showNotification({
          title: 'Уже открыто',
          message: 'Это обращение уже открыто.',
          color: 'blue',
        })
        return
      }
      closePreviousTicketsModal()
      openOrSelectTab(`/tickets/${encodeURIComponent(ticketPathSegmentFromListItem(ticket))}`)
    },
    [closePreviousTicketsModal, currentTicket?.id, openOrSelectTab],
  )

  const workspaceProjectsById = useMemo(
    () => new Map(workspaceProjects.map((project) => [project.id, project] as const)),
    [workspaceProjects],
  )

  const previousTicketsProjectCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const ticket of previousTickets) {
      const id = (ticket.project_id || '').trim() || '__unknown__'
      counts.set(id, (counts.get(id) || 0) + 1)
    }
    return [...counts.entries()].sort((left, right) => right[1] - left[1])
  }, [previousTickets])

  const sortedPreviousTickets = useMemo(
    () =>
      [...previousTickets].sort((left, right) => {
        const leftTime = parseApiDateUtc(left.created_at)?.getTime() ?? 0
        const rightTime = parseApiDateUtc(right.created_at)?.getTime() ?? 0
        return rightTime - leftTime
      }),
    [previousTickets],
  )

  const displayedPreviousTickets = useMemo(() => {
    if (sortedPreviousTickets.length <= previewLimit) return sortedPreviousTickets
    return sortedPreviousTickets.slice(0, previewLimit)
  }, [previewLimit, sortedPreviousTickets])

  const previousTicketTabStateById = useMemo(() => {
    const states = new Map<string, 'active' | 'open'>()
    for (const ticket of previousTickets) {
      const segment = ticketPathSegmentFromListItem(ticket)
      const match = findTabForDestination(tabs, `/tickets/${segment}`, '')
      if (match) states.set(ticket.id, match.id === activeTabId ? 'active' : 'open')
    }
    return states
  }, [activeTabId, previousTickets, tabs])

  const projectPickForPreviousList = useCallback(
    (
      projectId: string,
      fallbackName: string | null | undefined,
    ): Pick<ProjectRow, 'name' | 'color' | 'icon' | 'icon_url'> => {
      const id = (projectId || '').trim()
      if (!id) {
        return {
          name: (fallbackName || '').trim() || 'Неизвестный проект',
          color: 'var(--mantine-color-gray-5)',
          icon: null,
          icon_url: null,
        }
      }
      const project = workspaceProjectsById.get(id)
      if (project) return project
      return {
        name: (fallbackName || '').trim() || 'Проект',
        color: 'var(--mantine-color-gray-5)',
        icon: null,
        icon_url: null,
      }
    },
    [workspaceProjectsById],
  )

  return {
    displayedPreviousTickets,
    onSelectPreviousTicket,
    openPreviousTicketInNewTab,
    previousTicketsProjectCounts,
    previousTicketTabStateById,
    projectPickForPreviousList,
    sortedPreviousTickets,
  }
}
